import type { ResourceCacheStatus } from "../types";

export type FirstApiRequestDetectionMethod = "request_hook" | "resource_timing";

// One accumulated resource-timing entry (API fetch/XHR or static script/css/
// font/img) for the waterfall. Captured as it completes (so responseEnd is
// final), before the shared resource buffer can evict it. Shaped to the
// RequestTiming wire type by collectApiRequestTimings/collectResourceTimings.
export interface AccumulatedResourceEntry {
  name: string;
  startTime: number;
  responseEnd: number;
  initiatorType: string;
  cache?: ResourceCacheStatus;
}

export interface XviewPerfTimingRegistry {
  firstApiRequestStartTime?: number;
  firstApiRequestDetectionMethod?: FirstApiRequestDetectionMethod;
  firstApiRequestUrl?: string;
  firstApiRequestResponseEnd?: number;
  firstScriptUrl?: string;
  firstScriptStartTime?: number;
  firstScriptResponseEnd?: number;
  entryScriptResourceUrl?: string;
  entryScriptResourceStartTime?: number;
  entryScriptResourceResponseEnd?: number;
  appMainScriptResourceUrl?: string;
  appMainScriptResourceStartTime?: number;
  appMainScriptResourceResponseEnd?: number;
  excludedInstrumentationScriptUrls?: string[];
  // Long-lived accumulator for the request waterfalls (see observeResourceTimings).
  resourceTimingEntries?: AccumulatedResourceEntry[];
  resourceTimingsObserverInstalled?: boolean;
}

declare global {
  interface Window {
    __xviewPerfTiming__?: XviewPerfTimingRegistry;
  }
}

export interface FirstApiInfo {
  startTime: number;
  url: string;
  detectionMethod: FirstApiRequestDetectionMethod;
  responseEnd?: number;
}

export interface FirstApiTimingOptions {
  apiHostPatterns?: RegExp[];
  denyHostPatterns?: RegExp[];
  bufferSize?: number;
  onFirstApi?: (info: FirstApiInfo) => void;
}

export const ALLOWED_INITIATOR_TYPES: ReadonlySet<string> = new Set([
  "fetch",
  "xmlhttprequest",
]);

// Static resources accumulated for the waterfall: scripts, stylesheets
// (initiator "link"), CSS-loaded assets like fonts/background images
// (initiator "css"), and <img> loads. img entries are folded into the
// image_summary aggregate instead of getting individual bars (see
// collectApiRequestTimings.ts). Everything else (beacon, iframe, "other")
// stays out of the waterfall.
export const STATIC_INITIATOR_TYPES: ReadonlySet<string> = new Set([
  "script",
  "link",
  "css",
  "img",
]);

export const DEFAULT_API_HOST_PATTERNS: readonly RegExp[] = [
  /\.karrotmarket\.com$/,
  /\.daangn\.com$/,
  /^api\./,
  /^jsonplaceholder\.typicode\.com$/,
];

// Observability/analytics hosts: never APIs and never bars we want in a
// waterfall — their requests are instrumentation noise.
export const TRACKER_HOST_PATTERNS: readonly RegExp[] = [
  /^event-gateway-web\.(?:alpha\.)?(?:kr|jp|ca)\.karrotmarket\.com$/,
  /sentry\.io$/,
  /googletagmanager\.com$/,
  /google-analytics\.com$/,
];

// API-detection deny list: trackers plus static-asset CDN hosts (a cloudfront
// host is a fine image CDN but never an API origin). Static bars must NOT use
// this list — that would silently drop CDN-hosted assets from
// resource_timings; they use TRACKER_HOST_PATTERNS instead.
export const DEFAULT_DENY_HOST_PATTERNS: readonly RegExp[] = [
  /\.cloudfront\.net$/,
  /\.jsdelivr\.net$/,
  /\.unpkg\.com$/,
  ...TRACKER_HOST_PATTERNS,
];

const DEFAULT_BUFFER_SIZE = 500;

function getHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

export function isApiUrl(
  url: string,
  apiPatterns: readonly RegExp[],
  denyPatterns: readonly RegExp[],
  selfHost: string,
): boolean {
  const host = getHostname(url);
  if (host === null) return false;
  if (selfHost !== "" && host === selfHost) return false;
  if (denyPatterns.some((rx) => rx.test(host))) return false;
  if (apiPatterns.length === 0) return true;
  return apiPatterns.some((rx) => rx.test(host));
}

export function observeFirstApi(options: FirstApiTimingOptions = {}): void {
  if (typeof window === "undefined") return;
  if (typeof PerformanceObserver === "undefined") return;

  const apiHostPatterns = options.apiHostPatterns ?? DEFAULT_API_HOST_PATTERNS;
  const denyHostPatterns =
    options.denyHostPatterns ?? DEFAULT_DENY_HOST_PATTERNS;
  const bufferSize = options.bufferSize ?? DEFAULT_BUFFER_SIZE;

  try {
    performance.setResourceTimingBufferSize?.(bufferSize);
  } catch {
    /* no-op */
  }

  const ns = (window.__xviewPerfTiming__ ??= {} as XviewPerfTimingRegistry);

  // Fully captured (both startTime and responseEnd present) — nothing left to observe.
  // If startTime is set but responseEnd is absent, proceed to install the back-fill observer.
  if (
    ns.firstApiRequestStartTime != null &&
    ns.firstApiRequestResponseEnd != null
  )
    return;

  const selfHost = window.location?.host ?? "";

  const tryCapture = (entries: PerformanceResourceTiming[]): boolean => {
    // Back-fill branch: request_hook captured startTime but responseEnd is still pending.
    // Match by URL to fill in responseEnd from the PO entry without touching other fields.
    if (
      ns.firstApiRequestStartTime != null &&
      ns.firstApiRequestResponseEnd === undefined
    ) {
      const sorted = entries
        .filter((entry) => ALLOWED_INITIATOR_TYPES.has(entry.initiatorType))
        .sort((a, b) => a.startTime - b.startTime);
      for (const entry of sorted) {
        if (entry.name === ns.firstApiRequestUrl) {
          ns.firstApiRequestResponseEnd = entry.responseEnd;
          return true;
        }
      }
      return false;
    }

    if (ns.firstApiRequestStartTime != null) return true;

    const sorted = entries
      .filter((entry) => ALLOWED_INITIATOR_TYPES.has(entry.initiatorType))
      .sort((a, b) => a.startTime - b.startTime);

    for (const entry of sorted) {
      if (!isApiUrl(entry.name, apiHostPatterns, denyHostPatterns, selfHost)) {
        continue;
      }
      ns.firstApiRequestStartTime = entry.startTime;
      ns.firstApiRequestDetectionMethod = "resource_timing";
      ns.firstApiRequestUrl = entry.name;
      ns.firstApiRequestResponseEnd = entry.responseEnd;
      try {
        options.onFirstApi?.({
          startTime: entry.startTime,
          url: entry.name,
          detectionMethod: "resource_timing",
          responseEnd: entry.responseEnd,
        });
      } catch {
        /* user callback errors are isolated */
      }
      return true;
    }
    return false;
  };

  const installObserver = (init: PerformanceObserverInit): boolean => {
    try {
      const observer = new PerformanceObserver((list, obs) => {
        if (tryCapture(list.getEntries() as PerformanceResourceTiming[])) {
          obs.disconnect();
        }
      });
      observer.observe(init);
      return true;
    } catch {
      return false;
    }
  };

  if (!installObserver({ type: "resource", buffered: true })) {
    installObserver({ entryTypes: ["resource"] });
  }
}

export interface ObserveResourceTimingsOptions {
  apiHostPatterns?: readonly RegExp[];
  denyHostPatterns?: readonly RegExp[];
  // Memory bound on the accumulator. Pre-LCP volume is modest even on
  // media-heavy pages, so this only guards a pathological long-lived session;
  // the wire payload has its own, tighter per-field cap in collect*Timings.
  maxEntries?: number;
}

const DEFAULT_MAX_ACCUMULATED = 500;

// Cache revalidation can involve network traffic. Do not interpret 2 as a miss.
export function classifyResourceCache(entry: {
  transferSize?: number;
  decodedBodySize?: number;
  deliveryType?: string;
  workerStart?: number;
}): ResourceCacheStatus {
  // Service worker responses need separate instrumentation to identify cache use.
  if ((entry.workerStart ?? 0) > 0) return 0;
  if (entry.deliveryType === "cache") return 1;
  if (entry.transferSize === 0 && (entry.decodedBodySize ?? 0) > 0) return 1;
  if (Number.isFinite(entry.transferSize) && (entry.transferSize ?? 0) > 0)
    return 2;
  return 0;
}

// Long-lived resource observer that appends every waterfall-relevant request —
// API fetch/XHR (API-host filtered) plus static script/link/css/img (tracker-host
// filtered, instrumentation scripts excluded) — to `resourceTimingEntries` as
// each completes. Unlike a getEntriesByType() snapshot at flush time, this is
// immune to the shared resource-timing buffer evicting early entries once a
// media-heavy page fills it. collectApiRequestTimings/collectResourceTimings
// read this array and cut it off at the LCP render time.
export function observeResourceTimings(
  options: ObserveResourceTimingsOptions = {},
): void {
  if (typeof window === "undefined") return;
  if (typeof PerformanceObserver === "undefined") return;

  const apiHostPatterns = options.apiHostPatterns ?? DEFAULT_API_HOST_PATTERNS;
  const denyHostPatterns =
    options.denyHostPatterns ?? DEFAULT_DENY_HOST_PATTERNS;
  const maxEntries = options.maxEntries ?? DEFAULT_MAX_ACCUMULATED;
  const selfHost = window.location?.host ?? "";

  const ns = (window.__xviewPerfTiming__ ??= {} as XviewPerfTimingRegistry);
  if (ns.resourceTimingsObserverInstalled) return;
  const acc = (ns.resourceTimingEntries ??= []);

  // Static bars keep same-host assets and CDN-hosted assets (the API deny
  // list exists to say "cloudfront is not an API" — for static bars those
  // hosts are exactly what we want), so only tracker hosts and our own
  // instrumentation scripts are excluded here.
  const isDeniedStatic = (url: string): boolean => {
    const host = getHostname(url);
    if (host === null) return true;
    if (TRACKER_HOST_PATTERNS.some((rx) => rx.test(host))) return true;
    return ns.excludedInstrumentationScriptUrls?.includes(url) ?? false;
  };

  const consume = (entries: PerformanceResourceTiming[]): void => {
    for (const entry of entries) {
      if (acc.length >= maxEntries) return;
      const isApi = ALLOWED_INITIATOR_TYPES.has(entry.initiatorType);
      const isStatic = STATIC_INITIATOR_TYPES.has(entry.initiatorType);
      if (!isApi && !isStatic) continue;
      if (
        isApi &&
        !isApiUrl(entry.name, apiHostPatterns, denyHostPatterns, selfHost)
      ) {
        continue;
      }
      if (isStatic && isDeniedStatic(entry.name)) continue;
      acc.push({
        name: entry.name,
        startTime: entry.startTime,
        responseEnd: entry.responseEnd,
        initiatorType: entry.initiatorType,
        ...(isStatic && entry.initiatorType !== "img"
          ? { cache: classifyResourceCache(entry) }
          : {}),
      });
    }
  };

  const installObserver = (init: PerformanceObserverInit): boolean => {
    try {
      const observer = new PerformanceObserver((list) => {
        consume(list.getEntries() as PerformanceResourceTiming[]);
      });
      observer.observe(init);
      return true;
    } catch {
      return false;
    }
  };

  // Latch only on success: if both attempts throw (constructor or observe
  // unsupported), leave the flag unset so a later call — the IIFE and a bundled
  // copy both reach this — can retry, instead of silently disabling the
  // waterfall for the whole session.
  ns.resourceTimingsObserverInstalled =
    installObserver({ type: "resource", buffered: true }) ||
    installObserver({ entryTypes: ["resource"] });
}
